import React, { useState, useEffect } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const faqData = [
  {
    categoria: 'Produtos',
    perguntas: [
      {
        pergunta: 'Como são defumadas as vossas carnes?',
        resposta: 'As nossas carnes são defumadas artesanalmente em lenha selecionada, utilizando técnicas tradicionais que combinam o melhor da tradição portuguesa, brasileira e americana. O processo pode demorar entre 4 a 14 horas dependendo do corte.'
      },
      {
        pergunta: 'Quais são os cortes disponíveis?',
        resposta: 'Oferecemos uma variedade de cortes premium incluindo Brisket (peito de novilho), Ribs (entrecosto), Pulled Pork, Pulled Novilho, Cachaço de Porco, Pernas e Asinhas de Frango BBQ, além de produtos como Croquetes, Sais Temperados e Molhos BBQ.'
      },
      {
        pergunta: 'As carnes são frescas ou congeladas?',
        resposta: 'As nossas carnes são vendidas frescas ou ultracongeladas, dependendo da disponibilidade e preferência do cliente. A ultracongelação mantém todas as propriedades e sabor da carne por mais tempo.'
      },
      {
        pergunta: 'Qual é a validade dos produtos?',
        resposta: 'Os produtos frescos devem ser consumidos em até 5 dias se refrigerados. Os produtos ultracongelados mantêm a qualidade por até 6 meses quando armazenados corretamente a -18°C.'
      }
    ]
  },
  {
    categoria: 'Encomendas e Entregas',
    perguntas: [
      {
        pergunta: 'Como posso fazer uma encomenda?',
        resposta: 'Pode fazer a sua encomenda através do WhatsApp, telefone ou email. Contacte-nos para mais informações sobre disponibilidade e preços atualizados.'
      },
      {
        pergunta: 'Fazem entregas?',
        resposta: 'Sim, fazemos entregas na zona de Mafra e arredores. Para outras localizações, consulte-nos para verificar disponibilidade e condições.'
      },
      {
        pergunta: 'Qual é o prazo de entrega?',
        resposta: 'O prazo de entrega varia conforme a localização e disponibilidade de stock. Geralmente, as encomendas são entregues em 24 a 72 horas após confirmação.'
      },
      {
        pergunta: 'Existe um valor mínimo de encomenda?',
        resposta: 'Para entregas, existe um valor mínimo que pode variar conforme a localização. Contacte-nos para mais informações.'
      }
    ]
  },
  {
    categoria: 'Parcerias B2B',
    perguntas: [
      {
        pergunta: 'Fornecem para restaurantes?',
        resposta: 'Sim! Trabalhamos com diversos parceiros na restauração, incluindo restaurantes, cafés, bares e hotéis. Oferecemos condições especiais para parceiros B2B.'
      },
      {
        pergunta: 'Fazem catering para eventos?',
        resposta: 'Sim, oferecemos soluções personalizadas de catering para eventos corporativos, casamentos, festas privadas e outros eventos especiais.'
      },
      {
        pergunta: 'Como posso tornar-me parceiro?',
        resposta: 'Entre em contacto connosco através do formulário de contacto ou WhatsApp. Teremos todo o gosto em apresentar as nossas condições de parceria.'
      }
    ]
  },
  {
    categoria: 'Outros',
    perguntas: [
      {
        pergunta: 'Onde estão localizados?',
        resposta: 'A nossa fábrica está localizada em Mafra, Portugal. O endereço completo e horário de funcionamento podem ser consultados na página de Contacto.'
      },
      {
        pergunta: 'Têm loja física?',
        resposta: 'Trabalhamos principalmente por encomenda, mas pode visitar-nos na nossa fábrica mediante marcação prévia.'
      },
      {
        pergunta: 'Os produtos são adequados para pessoas com alergias?',
        resposta: 'Alguns dos nossos produtos podem conter alergénios. Consulte sempre a informação do produto ou contacte-nos para esclarecer qualquer dúvida sobre ingredientes específicos.'
      }
    ]
  }
];

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});
  const [seoData, setSeoData] = useState({
    meta_title: 'FAQ - Santa Carne BBQ',
    meta_description: 'Perguntas frequentes sobre os produtos e serviços da Santa Carne BBQ.'
  });

  useEffect(() => {
    // Fetch SEO settings
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/settings`);
        if (response.ok) {
          const data = await response.json();
          document.title = `FAQ - ${data.site_name || 'Santa Carne'}`;
        }
      } catch (err) {
        console.error('Erro ao carregar configurações:', err);
      }
    };
    fetchSettings();
  }, []);

  const toggleItem = (categoria, index) => {
    const key = `${categoria}-${index}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] pt-20">
      {/* Hero Section */}
      <section className="relative py-20 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#242424] to-[#1a1a1a]" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 z-10 text-center">
          <p className="text-[#B8860B] text-sm tracking-[0.3em] uppercase mb-4">Ajuda</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold text-[#E8DDCB] mb-6" style={{fontFamily: "'Playfair Display', serif"}}>
            Perguntas Frequentes
          </h1>
          <div className="w-16 h-[2px] bg-[#B8860B] mx-auto mb-6" />
          <p className="text-[#E8DDCB]/70 text-lg max-w-2xl mx-auto">
            Encontre respostas às perguntas mais comuns sobre os nossos produtos e serviços.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          {faqData.map((categoria, catIndex) => (
            <div key={catIndex} className="mb-12">
              <h2 className="text-2xl font-semibold text-[#B8860B] mb-6" style={{fontFamily: "'Playfair Display', serif"}}>
                {categoria.categoria}
              </h2>
              <div className="space-y-4">
                {categoria.perguntas.map((item, itemIndex) => {
                  const key = `${categoria.categoria}-${itemIndex}`;
                  const isOpen = openItems[key];
                  
                  return (
                    <div 
                      key={itemIndex}
                      className="border border-[#B8860B]/20 rounded-lg overflow-hidden"
                    >
                      <button
                        onClick={() => toggleItem(categoria.categoria, itemIndex)}
                        className="w-full flex items-center justify-between p-5 text-left bg-[#242424] hover:bg-[#2a2a2a] transition-colors"
                      >
                        <span className="text-[#E8DDCB] font-medium pr-4">{item.pergunta}</span>
                        <ChevronDown 
                          className={`w-5 h-5 text-[#B8860B] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      <div 
                        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}
                      >
                        <p className="p-5 text-[#E8DDCB]/70 bg-[#1f1f1f] leading-relaxed">
                          {item.resposta}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#242424]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-semibold text-[#E8DDCB] mb-4" style={{fontFamily: "'Playfair Display', serif"}}>
            Não encontrou o que procurava?
          </h2>
          <p className="text-[#E8DDCB]/70 mb-8">
            Entre em contacto connosco e teremos todo o gosto em ajudar.
          </p>
          <a 
            href="/contacto"
            className="inline-flex items-center px-8 py-3 bg-[#B8860B] hover:bg-[#D4A84B] text-[#1a1a1a] font-semibold rounded-lg transition-colors"
          >
            Contactar
          </a>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
